import { useMemo, useState, type DragEvent } from 'react';
import { localizedName, WEIGHT_MAX } from '../data/positions.ts';
import { DEPTH_POSITIONS } from '../lib/depth.ts';
import { decimal, suitability, TARGET_DEPTH } from '../lib/rules.ts';
import { useSquad } from '../lib/squadContext.ts';
import { C, fitBand } from '../lib/theme.ts';
import type { PositionCode } from '../types.ts';

interface DepthChip {
  id: string;
  name: string;
  rating: string;
  /** False once the user has dragged him away from his dataset position. */
  atDatasetPosition: boolean;
  /** His weight at the row he is filed under, and the band it paints. */
  weight: number;
  line: string;
  wash: string;
  dashed: boolean;
}

interface DepthRow {
  code: PositionCode;
  name: string;
  count: number;
  status: string;
  statusFg: string;
  countFg: string;
  rowBg: string;
  players: DepthChip[];
}

/**
 * The squad's depth, one row per position — and the one place the user can re-file
 * players by hand. Dragging a chip to another row changes where that player is picked;
 * dragging within a row changes his rank. SIFIRLA drops back to the dataset.
 */
export default function DepthChart() {
  const { state, dispatch, players, firstTeam, depthOrder, shortName, playerHandles } = useSquad();
  const [hoverRow, setHoverRow] = useState<PositionCode | null>(null);

  const rows = useMemo<DepthRow[]>(() => {
    const inFirstTeam = new Map(firstTeam.map((p) => [p.id, p]));
    // The untouched dataset, so a hand-moved player can be shown as such.
    const datasetPrimary = new Map(state.players.map((p) => [p.id, p.positions[0]]));

    return DEPTH_POSITIONS.map((code) => {
      const listed = (depthOrder[code] ?? [])
        .map((id) => inFirstTeam.get(id))
        .filter((p): p is NonNullable<typeof p> => Boolean(p));
      const count = listed.length;
      const status =
        count === 0
          ? 'BOŞ'
          : count < TARGET_DEPTH
            ? 'ZAYIF'
            : count > TARGET_DEPTH + 2
              ? 'ŞİŞKİN'
              : 'DENGELİ';

      return {
        code,
        name: localizedName(code),
        count,
        status,
        statusFg: status === 'BOŞ' || status === 'ZAYIF' ? C.deep : C.muted,
        countFg: status === 'BOŞ' || status === 'ZAYIF' ? C.red : C.ink,
        rowBg: status === 'BOŞ' ? C.tint : C.bg,
        players: listed.map((p) => {
          const band = fitBand(suitability(p, code));
          return {
            id: p.id,
            name: shortName(p),
            rating: decimal(p.rating, 2),
            atDatasetPosition: datasetPrimary.get(p.id) === code,
            weight: band.weight,
            line: band.line,
            wash: band.wash,
            dashed: band.dashed,
          };
        }),
      };
    });
  }, [state.players, players, firstTeam, depthOrder, shortName]);

  const allowDrop = (code: PositionCode) => (event: DragEvent) => {
    event.preventDefault();
    try {
      event.dataTransfer.dropEffect = 'move';
    } catch {
      // Firefox refuses dropEffect on some drags; the drop still works.
    }
    if (hoverRow !== code) setHoverRow(code);
  };

  /** Drop onto a chip files the player immediately before it; onto the row, at the end. */
  const handleDrop = (code: PositionCode, beforeId: string | null) => (event: DragEvent) => {
    event.preventDefault();
    event.stopPropagation();
    setHoverRow(null);
    const id = event.dataTransfer.getData('text/plain') || state.draggingId;
    if (!id) return;
    // Same reason as on the pitch: the move re-renders the chip that started the drag,
    // so its own `onDragEnd` cannot be relied on to clear the state.
    dispatch({ type: 'moveInDepth', id, position: code, beforeId });
    dispatch({ type: 'dragEnd' });
  };

  const edited = state.depthOrder !== null;

  return (
    <div style={{ minWidth: 0 }} onDragLeave={() => setHoverRow(null)}>
      <div
        style={{
          padding: '10px 16px',
          borderBottom: '1px solid ' + C.line,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          gap: 12,
        }}
      >
        <span style={{ fontSize: 10, fontWeight: 800, letterSpacing: '0.14em' }}>
          MEVKİ BAZLI DERİNLİK
        </span>
        <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
          <span style={{ fontSize: 10, fontWeight: 600, color: C.muted }}>
            {edited
              ? 'Elle düzenlendi · sürükleyip bırakarak değiştir'
              : 'Hedef: her mevkide en az ' + TARGET_DEPTH + ' oyuncu'}
          </span>
          <button
            onClick={() => dispatch({ type: 'resetDepth' })}
            disabled={!edited}
            title="Mevki dağılımını veri setindeki hâline döndür"
            style={{
              border: '2px solid ' + (edited ? C.ink : C.line),
              background: C.bg,
              color: edited ? C.ink : C.line,
              fontSize: 9.5,
              fontWeight: 800,
              letterSpacing: '0.1em',
              padding: '4px 9px',
              cursor: edited ? 'pointer' : 'not-allowed',
            }}
          >
            SIFIRLA
          </button>
        </div>
      </div>

      {rows.map((row) => (
        <div
          key={row.code}
          onDragOver={allowDrop(row.code)}
          onDrop={handleDrop(row.code, null)}
          style={{
            display: 'flex',
            alignItems: 'flex-start',
            gap: 12,
            padding: '9px 16px',
            borderBottom: '1px solid #dcd9d7',
            background: hoverRow === row.code ? C.tint : row.rowBg,
            outline: hoverRow === row.code ? '2px solid ' + C.red : 'none',
            outlineOffset: '-2px',
          }}
        >
          <div style={{ width: 112, flex: '0 0 112px' }}>
            <div style={{ fontSize: 11.5, fontWeight: 800, letterSpacing: '0.02em' }}>
              {row.name}
            </div>
            <div
              style={{
                fontSize: 9.5,
                fontWeight: 700,
                letterSpacing: '0.1em',
                color: row.statusFg,
              }}
            >
              {row.status}
            </div>
          </div>
          <div
            style={{
              width: 46,
              flex: '0 0 46px',
              textAlign: 'right',
              fontSize: 19,
              fontWeight: 800,
              lineHeight: 1,
              color: row.countFg,
            }}
          >
            {row.count}
          </div>
          <div
            style={{
              flex: 1,
              display: 'flex',
              flexWrap: 'wrap',
              gap: 4,
              minWidth: 0,
              minHeight: 20,
            }}
          >
            {row.players.map((chip) => (
              <span
                key={chip.id}
                {...playerHandles(chip.id)}
                onDragOver={allowDrop(row.code)}
                onDrop={handleDrop(row.code, chip.id)}
                title={
                  row.name +
                  ' ağırlığı ' +
                  chip.weight +
                  '/' +
                  WEIGHT_MAX +
                  (chip.atDatasetPosition ? '' : ' · bu mevkiye elle taşındı') +
                  ' · sürükleyip taşı ya da sırasını değiştir'
                }
                style={{
                  fontSize: 10.5,
                  fontWeight: 700,
                  padding: '2px 6px',
                  // Same scale as the pitch: the chip says how well he covers the row he
                  // is filed under, so a player who is gold there is gold here too.
                  background: chip.wash,
                  color: C.ink,
                  border: '2px ' + (chip.dashed ? 'dashed ' : 'solid ') + chip.line,
                  cursor: 'grab',
                  whiteSpace: 'nowrap',
                }}
              >
                {chip.name} · {chip.rating}
              </span>
            ))}
            {row.count === 0 && (
              <span style={{ fontSize: 10.5, fontWeight: 700, color: C.deep }}>
                Bu mevkide oyuncu yok.
              </span>
            )}
          </div>
        </div>
      ))}
    </div>
  );
}
